import {addOrReplaceAndRunAnalysisStep, updateAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {isViewTerminalStepType} from 'analysis-tool/data/LineageStepTypes';
import {getAncestorStepsByType} from 'analysis-tool/header-options/DataFrameHeaderMenuUtil';
import {getReadOnlyInfo} from 'analysis-tool/header-options/GetDataFrameHeaderMenuOptions';
import {DataReference} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assert} from 'Asserts';
import {DataFrameColumnDef, DataFrameSpec} from 'data-frames/DataFrameTypes';
import {DataFrameFilterOptions, FilterWithMetadata} from 'data-frames/filters/DataFrameFilterOptions';
import {DataFrameFilter, DataFrameFilterOperation} from 'data-frames/filters/DataFrameFilterTypes';
import {DataFrameReadOnlyInfo} from 'data-frames/header-menu/DataFrameReadOnlyInfo';
import getUserColumns from 'data-frames/util/getUserColumns';
import {
  PipelineStepStatusCode,
  PipelineStepType,
  PipelineStepDataKind,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {FILTER_TABLE_STARTED_EVENT} from 'performance/processAnalysesEvents';
import {getParentSteps, getTableLineageAncestorSteps} from 'pipelines/StepLineageUtils';
import {withLoadingScreen} from 'util/Promises';

const LOADING_MESSAGE = 'Updating filter';

const DATA_FRAME_FILTER_OPTIONS_FOR_IN_PROGRESS_STEP: DataFrameFilterOptions = {
  getFilterForColumnHeaders: () => null,
  getFilterForColumn: () => null,
  // eslint-disable-next-line require-await
  updateFilterForColumn: async () => undefined,
  dataFrameSpec: {columns: [], name: 'Unknown'},
  getFiltersWithMetadata: () => [],
  getUnfilteredColumns: () => [],
};

const FILTER_BADGE_INHERITING_STEP_TYPES = new Set<PipelineStepType>([
  PipelineStepType.HIDE_COLUMNS,
  PipelineStepType.FILTER_DATASET,
  PipelineStepType.DATASET_VIEW,
]);

export function makeFilterId(stepId: string, index: number): string {
  return `${stepId}-${index}`;
}

export function getStepIdFromFilterId(filterId: string): string {
  return filterId.split('-')[0];
}

function filterMatchesColumn(filter: DataFrameFilter, columnSystemName: string): boolean {
  return filter.filters.every((filterCondition) => filterCondition.columnSystemName === columnSystemName);
}

function getAllFilters(filterStep: GetAnalysisPipelineStep | null): Array<DataFrameFilter> | null {
  const config = filterStep?.config?.variables.find((variable) => variable.name === 'filter');
  return (config?.value?.filters ?? null) as Array<DataFrameFilter> | null;
}

function getUpdatedAllFilters(
  allFilters: Array<DataFrameFilter>,
  columnSystemName: string,
  newFilterForColumn: DataFrameFilter | null
): Array<DataFrameFilter> {
  const filtersNotOnColumnToUpdate = allFilters.filter(
    (filter) => !filterMatchesColumn(filter, columnSystemName)
  );
  if (newFilterForColumn) {
    return [...filtersNotOnColumnToUpdate, newFilterForColumn];
  } else {
    return filtersNotOnColumnToUpdate;
  }
}

async function addFilterStep(
  analysis: AnalysisGraphQL,
  filters: Array<DataFrameFilter>,
  inputDataReference: DataReference,
  inputDataFrameId?: string,
  outputDataReferences?: Array<DataReference>
): Promise<void> {
  await addOrReplaceAndRunAnalysisStep(analysis.id, {
    stepType: PipelineStepType.FILTER_DATASET,
    stepVersion: 0,
    stepConfig: {filter: {operation: DataFrameFilterOperation.AND, filters}},
    stepInputParents: {[SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [inputDataReference]},
    ...(inputDataFrameId == null ? {} : {stepInput: {inputDataFrameId}}),
    ...(outputDataReferences == null
      ? {}
      : {stepOutputChildren: {[SINGLE_DATASET_OUTPUT_STEP_DATA_NAME]: outputDataReferences}}),
  });
}

async function updateFilterStep(
  analysis: AnalysisGraphQL,
  filterStep: GetAnalysisPipelineStep,
  filters: Array<DataFrameFilter>
): Promise<void> {
  await updateAndRunAnalysisStep(analysis.id, {
    stepId: filterStep.id,
    stepConfig: {filter: {operation: DataFrameFilterOperation.AND, filters}},
  });
}

function getFilterStepsForBadgeDisplay(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep
): Array<GetAnalysisPipelineStep> {
  const steps = getTableLineageAncestorSteps(analysis, step);
  const filterSteps = [];
  let index = steps.length - 1;
  while (index >= 0 && FILTER_BADGE_INHERITING_STEP_TYPES.has(steps[index].definition.stepType)) {
    if (steps[index].definition.stepType === PipelineStepType.FILTER_DATASET) {
      filterSteps.push(steps[index]);
    }
    index--;
  }
  return filterSteps;
}

/**
 * Used to get filter and the filter step it is on so we can display the filter badge on the column header.
 * We only show the most recent filter for a column, and a filter that is not followed by transformations
 * that break badge inheritance.
 * @param filterStepsForBadgeDisplay List of filter steps that are not followed by transformations that break badge inheritance.
 * @param columnSystemName Column to show a badge for.
 * @returns The filter and the filter step it is on, or null if there is no filter for the column.
 */
function getFilterInfoForColumnBadge(
  filterStepsForBadgeDisplay: Array<GetAnalysisPipelineStep>,
  columnSystemName: string
): null | {filter: DataFrameFilter; filterStep: GetAnalysisPipelineStep} {
  // Walk through filter steps, and return the filterStep and filter of the first step that applies to this column
  for (const filterStep of filterStepsForBadgeDisplay) {
    const allFilters = getAllFilters(filterStep) ?? [];
    const maybeFilter =
      allFilters.find((filter: DataFrameFilter) => filterMatchesColumn(filter, columnSystemName)) ?? null;
    if (maybeFilter) {
      return {filter: maybeFilter, filterStep};
    }
  }
  return null;
}

/**
 * This dataframe spec is used by the add filter buttons so we know which columns are available to filter on.
 */
function getOutputDataFrameSpec(
  step: GetAnalysisPipelineStep,
  inputDataReference: DataReference,
  inputDataFrameId: string,
  filterStepsForBadgeDisplay: Array<GetAnalysisPipelineStep>
): DataFrameSpec {
  let dataFrame = step.outputStepDatas
    .find((stepData) => stepData.name === inputDataReference.stepDataName)
    ?.dataFrames.find((dataFrame) => dataFrame.id === inputDataFrameId);

  if (dataFrame == null) {
    // After updating a filter step, the output dataframe is null until a refresh. The dataframe spec is the same for the input/output of a filter step, so we can use its input
    assert(
      filterStepsForBadgeDisplay.length > 0,
      'There must be an output frame if there are no active filter steps'
    );
    dataFrame = filterStepsForBadgeDisplay[0].inputStepDatas[0].dataFrames[0];
    assert(dataFrame != null, 'Data frame cannot be null');
  }
  return {
    columns: dataFrame.allColumnsJson,
    name: dataFrame.name,
  };
}
/**
 * Make functions to render and update a filters on data frames.
 *
 * @param analysis The analysis containing a data frame that may be filtered
 * @param step Step being rendered (usually the terminal step of a top-level dataset or view lineage).
 * @param inputDataReference The step ID and step data name from which the dataFrame came
 * @param inputDataFrameId The ID of the dataFrame mentioned above. Must be specified if inputDataReference
 *                         refers to an isMulti step data, in which case we can't infer the data frame
 * @param outputDataReferences If a new filter step needs to be placed before one or more existing steps, the
 *                             existing step ID and step data name into which the filtered dataset should go.
 */
export default function getDataFrameFilterOptions(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep,
  inputDataReference: DataReference,
  inputDataFrameId?: string,
  outputDataReferences?: Array<DataReference>
): DataFrameFilterOptions {
  if (
    step.definition.stepType === PipelineStepType.FILTER_DATASET &&
    step.statusCode === PipelineStepStatusCode.BLOCKED
  ) {
    // If the current step is a filter step and it is blocked, we can't update or add a filter step.
    return DATA_FRAME_FILTER_OPTIONS_FOR_IN_PROGRESS_STEP;
  } else if (
    step.definition.stepType !== PipelineStepType.FILTER_DATASET &&
    step.statusCode !== PipelineStepStatusCode.SUCCEEDED
  ) {
    // If the current step is not a filter step and it is not succeeded, we don't
    // want to allow users to try to add or update new filters.
    return DATA_FRAME_FILTER_OPTIONS_FOR_IN_PROGRESS_STEP;
  }
  const filterStepsForBadgeDisplay = getFilterStepsForBadgeDisplay(analysis, step);
  const getFilterForColumnHeaders = (columnSystemName: string): DataFrameFilter | null =>
    getFilterInfoForColumnBadge(filterStepsForBadgeDisplay, columnSystemName)?.filter ?? null;

  const inputStep = getLegacyStepFromAnalysis(analysis, inputDataReference.stepId);

  inputDataFrameId =
    inputDataFrameId ?? findAndAssertSingleStepDataFrameId(inputStep, inputDataReference.stepDataName);

  // dataframe spec is the dataframe of the current step's output
  const dataFrameSpec = getOutputDataFrameSpec(
    inputStep,
    inputDataReference,
    inputDataFrameId,
    filterStepsForBadgeDisplay
  );

  const ancestorFilterSteps = getAncestorStepsByType(analysis, step, PipelineStepType.FILTER_DATASET);

  const allFiltersWithMetadata = ancestorFilterSteps.flatMap((step) =>
    getFilterWithMetadataForStep(analysis, step)
  );

  const updateFilterForColumn = async (
    columnSystemName: string,
    filter: DataFrameFilter | null,
    filterId?: string
  ): Promise<void> => {
    assert(filter == null || filterMatchesColumn(filter, columnSystemName), 'Filter does not match column.');

    logProfilerTimingEvent(FILTER_TABLE_STARTED_EVENT);

    if (filterId) {
      // If a filterId is provided, update a particular filter
      const allFiltersForCurrentStep = allFiltersWithMetadata
        .concat(allTopLevelFiltersWithMetadata)
        .filter((filterWithMetadata) => filterWithMetadata.filterId.split('-')[0] === filterId.split('-')[0])
        .map((filterWithMetadata) => filterWithMetadata.filter);
      const newFilters = getUpdatedAllFilters(allFiltersForCurrentStep, columnSystemName, filter);
      const stepId = getStepIdFromFilterId(filterId);
      const updateStep = ancestorFilterSteps.concat(topLevelFilterSteps).find((step) => step.id === stepId);
      assert(updateStep != null, 'Expected step to be found');
      await withLoadingScreen(updateFilterStep(analysis, updateStep, newFilters), LOADING_MESSAGE);
    } else if (step.definition.stepType === PipelineStepType.FILTER_DATASET) {
      // If there is no existing column for this filter, and the last step is a filter step, we will add this new filter it
      const newFilters = getUpdatedAllFilters(getAllFilters(step) ?? [], columnSystemName, filter);
      await withLoadingScreen(updateFilterStep(analysis, step, newFilters), LOADING_MESSAGE);
    } else {
      // If the user cleared all conditions before applying the new filter, simply no-op
      if (filter == null) {
        return;
      }

      await withLoadingScreen(
        addFilterStep(analysis, [filter], inputDataReference, inputDataFrameId, outputDataReferences),
        LOADING_MESSAGE
      );
    }
  };

  const getUnfilteredColumns = (dataFrameSpec: DataFrameSpec): Array<DataFrameColumnDef> => {
    const columnsFilteredAtStep =
      step.definition.stepType === PipelineStepType.FILTER_DATASET
        ? (getAllFilters(step) ?? []).flatMap((filter) =>
            filter.filters.map((filter) => filter.columnSystemName)
          )
        : [];
    return getUserColumns(dataFrameSpec).filter(
      (column) => !columnsFilteredAtStep.includes(column.systemName)
    );
  };

  const getFilterForColumn = (columnSystemName: string, filterId: string): DataFrameFilter | null =>
    allFiltersWithMetadata.find(
      (filterWithMetadata: FilterWithMetadata) =>
        filterWithMetadata.filterId === filterId &&
        filterMatchesColumn(filterWithMetadata.filter, columnSystemName)
    )?.filter ?? null;

  const datasetViewStep = getTableLineageAncestorSteps(analysis, step).find(
    (step) => step.definition.stepType === PipelineStepType.DATASET_VIEW
  );
  const topLevelFilterSteps = datasetViewStep ? getTopLevelViewFilterSteps(analysis, datasetViewStep) : [];
  const allTopLevelFiltersWithMetadata = topLevelFilterSteps.flatMap((step) =>
    getFilterWithMetadataForStep(analysis, step)
  );

  const getFiltersWithMetadata = ({
    includeTopLevelFilterSteps = false,
    includeViewFilterSteps = true,
  }: {
    includeTopLevelFilterSteps?: boolean;
    includeViewFilterSteps?: boolean;
  }): Array<FilterWithMetadata> => {
    const filtersWithMetadata: Array<FilterWithMetadata> = [];

    if (includeTopLevelFilterSteps) {
      filtersWithMetadata.push(...allTopLevelFiltersWithMetadata);
    }
    if (includeViewFilterSteps) {
      filtersWithMetadata.push(...allFiltersWithMetadata);
    }
    return filtersWithMetadata;
  };

  return {
    getFilterForColumnHeaders,
    updateFilterForColumn,
    getFilterForColumn,
    getFiltersWithMetadata,
    getUnfilteredColumns,
    dataFrameSpec,
  };
}

/** @package */
export function getTopLevelViewFilterSteps(
  analysis: AnalysisGraphQL,
  datasetViewStep: GetAnalysisPipelineStep
): Array<GetAnalysisPipelineStep> {
  assert(
    datasetViewStep.definition.stepType === PipelineStepType.DATASET_VIEW,
    'Expected datasetViewStep to be DATASET_VIEW.'
  );
  const parentStepsOfDatasetViewStep = getParentSteps(analysis, datasetViewStep);
  assert(parentStepsOfDatasetViewStep.length === 1, 'Expected dataset view step to have exactly one parent.');
  return getAncestorStepsByType(analysis, parentStepsOfDatasetViewStep[0], PipelineStepType.FILTER_DATASET);
}

export function getFilterWithMetadataForStep(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep
): Array<FilterWithMetadata> {
  assert(step.definition.stepType === PipelineStepType.FILTER_DATASET, 'Expected step to be a filter step');

  const allFiltersForCurrentStep = getAllFilters(step) ?? [];
  const dataFrame = step.inputStepDatas[0].dataFrames[0];

  return allFiltersForCurrentStep.map((filter, index) => ({
    filterId: makeFilterId(step.id, index),
    filter,
    failed: step.statusCode === PipelineStepStatusCode.FAILED,
    running: step.statusCode === PipelineStepStatusCode.RUNNING,
    readOnlyInfo: getReadOnlyInfo(analysis, step),
    dataFrameSpec: {
      columns: dataFrame.allColumnsJson,
      name: dataFrame.name,
    },
  }));
}

/**
 * This function is used to generate filter options for a chart, regression, or table views.
 *
 * In a view, there may be multiple filter and transformation steps interspersed. We add filters
 * directly to the last top level step if it is a filter step, and otherwise add a new step right before
 * chart, regression, or view.
 */
export function getDataFrameFilterOptionsForView(
  analysis: AnalysisGraphQL,
  viewSteps: Array<GetAnalysisPipelineStep>
): {filterOptions: DataFrameFilterOptions; readOnlyInfo: DataFrameReadOnlyInfo} {
  const lastStepInView = viewSteps[viewSteps.length - 1];
  const terminalViewStep = isViewTerminalStepType(lastStepInView.definition.stepType) ? lastStepInView : null;
  const precedingStep = terminalViewStep == null ? lastStepInView : viewSteps[viewSteps.length - 2];

  let outputDataReferences;
  const precedingDatasetOutputStepDatas = precedingStep.outputStepDatas.filter(
    (stepData) => stepData.kind === PipelineStepDataKind.DATASET
  );
  if (precedingStep.definition.stepType === PipelineStepType.FILTER_DATASET) {
    outputDataReferences = [
      {
        stepId: precedingStep.id,
        stepDataName: SINGLE_DATASET_INPUT_STEP_DATA_NAME,
      },
    ];
  } else {
    assert(
      precedingDatasetOutputStepDatas.length === 1,
      'Expected the step preceding a filter step in a view lineage to have a single output step data.'
    );
    outputDataReferences =
      terminalViewStep == null
        ? undefined
        : [
            {
              stepId: terminalViewStep.id,
              stepDataName: SINGLE_DATASET_INPUT_STEP_DATA_NAME,
            },
          ];
  }
  const inputDataFrameId =
    precedingStep.definition.stepType === PipelineStepType.FILTER_DATASET
      ? precedingStep.inputStepDatas[0]?.dataFrames[0].id
      : undefined;

  // Filter options is used to add new filters. If the last step is a filter step, we want to reuse it,
  // but otherwise we will add a new filter step.
  const filterOptions = getDataFrameFilterOptions(
    analysis,
    precedingStep,
    {
      stepId: precedingStep.id,
      stepDataName: precedingDatasetOutputStepDatas[0]?.name,
    },
    inputDataFrameId,
    outputDataReferences
  );
  const readOnlyInfo = getReadOnlyInfo(analysis, precedingStep);

  return {filterOptions, readOnlyInfo};
}
